import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { EditSolidIcon } from '@dhl-ui/icons'
import { getActionTypeLabel, useGetAdminActionsActionid } from '@dhl/receiver-dhl-extra-api'
import { Body1, Body2, Box, Card, Grid, H6, IconButton } from '@dhl-ui/components'
import { BackButton } from '~/temp-ds-component/BackButton'
import { DangerZoneCard } from '~/common/components'
import { useNavigateWithSearchString } from '~/common/hooks'
import { links } from '~/AppRoutes'
import { PromotionDangerZoneDialog } from '~/modules/dhl-extra/components'

type Params = {
  id: string
}

export const PromotionDetailsPage = () => {
  const navigateWithSearch = useNavigateWithSearchString(true)
  const { t } = useTranslation()
  const { id } = useParams<Params>()
  const [dangerZoneOpen, setDangerZoneOpen] = useState<boolean>(false)
  const { data: promotion } = useGetAdminActionsActionid(id!, {
    query: {
      enabled: !!id,
    },
  })

  const dangerZoneContent = {
    title: t('admin.dhlExtraPage.promotionsPage.dangerZone.header'),
    subtitle: t('admin.dhlExtraPage.promotionsPage.dangerZone.subHeader'),
    value: t('admin.dhlExtraPage.promotionsPage.dangerZone.message'),
    button: t('admin.dhlExtraPage.promotionsPage.dangerZone.deleteButton'),
    id,
  }

  const handleDangerZoneClick = () => setDangerZoneOpen(true)

  const onBack = () => navigateWithSearch(links.promotionsOverviewLink())

  const formatDateTime = (value: string) => (value ? dayjs(value).format('DD MMMM YYYY') : '')

  if (!id || !promotion) {
    return null
  }

  return (
    <>
      <Box marginBottom={2}>
        <BackButton onClick={onBack}>{t('admin.organisationsPage.organisationForm.backButtonTitle')}</BackButton>
      </Box>
      <Grid container direction="row" spacing={3}>
        <Grid size={{ sm: 6 }}>
          <Box mb={2}>
            <Card>
              <Card.Header
                title={`${t('admin.dhlExtraPage.promotionsPage.promotionForm.detailsTitle')} ${promotion.title}`}
                endNode={
                  <>
                    <Grid container direction="row" spacing={1}>
                      <Grid>
                        <IconButton
                          aria-label="edit promotion"
                          onClick={() => navigateWithSearch(links.editPromotionLink(id))}
                        >
                          <EditSolidIcon data-testid="edit-dhlExtra-promotion" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </>
                }
              />
              <Card.Content>
                <Box mb={2}>
                  <H6>{t('admin.dhlExtraPage.promotionsPage.promotionForm.subTitle')}</H6>
                </Box>
                <Grid container direction="row" spacing={1}>
                  <Grid>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.title')}</Body2>
                    <Body1>{promotion.title}</Body1>
                  </Grid>
                  <Grid size={{ sm: 12 }}>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.description')}</Body2>
                    <Body1>{promotion.description}</Body1>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.startAt')}</Body2>
                    <Body1>{formatDateTime(promotion.startAt)}</Body1>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.endAt')}</Body2>
                    <Body1>{formatDateTime(promotion.endAt)}</Body1>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.actionType')}</Body2>
                    <Body1>{`${getActionTypeLabel(`${promotion.actionType}`)}`}</Body1>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Body2 variant="tertiary">{t('admin.dhlExtraPage.promotionsPage.promotionForm.contentId')}</Body2>
                    <Body1>{promotion.contentId}</Body1>
                  </Grid>
                </Grid>
              </Card.Content>
            </Card>
          </Box>
        </Grid>
      </Grid>
      <Grid size={{ sm: 6 }}>
        <Box marginTop={3}>
          <DangerZoneCard dangerZoneContent={dangerZoneContent} onClickOpen={handleDangerZoneClick} />
        </Box>
      </Grid>
      <PromotionDangerZoneDialog open={dangerZoneOpen} id={id} onClose={() => setDangerZoneOpen(false)} />
    </>
  )
}
